/**
 * Seleção FIXA de um candidato para uma restrição do pedido ("Escolher este
 * local"). Módulo puro, partilhado entre browser e servidor.
 *
 * Contrato:
 *  - `requestSignature` identifica o PEDIDO ORIGINAL normalizado (texto,
 *    localidade, dia/janela e restrições). NUNCA inclui área ativa, seleções
 *    fixas, cartões de disponibilidade nem estado temporário — alargar a zona
 *    ou escolher um local mantém a assinatura, pelo que a seleção nunca se
 *    invalida a si própria.
 *  - `constraintId` é estável: deriva da restrição normalizada + assinatura,
 *    nunca do índice do array.
 *  - O browser envia apenas `{ requestSignature, constraintId, placeId }`;
 *    o servidor re-confirma tudo via Place Details (coordenadas, tipos,
 *    horário) contra a geometria da área alargada confirmada.
 */

import {
  expandedAreaLabel,
  haversineMeters,
  normalizeLocality,
  type ExpandedArea,
} from "./locality-scope";
import type { RequiredStopConstraint } from "./required-constraints";

/**
 * Teto do alargamento DESTA etapa (candidato próximo confirmado pela pessoa).
 * Para lá disto, a app tem de propor um NOVO alargamento explícito.
 */
export const MAX_PINNED_AREA_RADIUS_METERS = 25000;
/** Margem para o local escolhido não ficar colado ao limite da área. */
export const PINNED_AREA_BUFFER_METERS = 800;

/** O que o browser envia ao servidor: identidade + referência assinada. */
export type PinnedSelectionRef = {
  requestSignature: string;
  constraintId: string;
  placeId: string;
  /**
   * Token opaco emitido pelo servidor quando o candidato apareceu no cartão.
   * A revalidação só aceita seleções com esta referência — nunca um placeId
   * escolhido livremente pelo cliente.
   */
  selectionRef: string;
};

/** Seleção completa guardada no cliente (a parte extra é apenas visual). */
export type PinnedConstraintSelection = PinnedSelectionRef & {
  name: string;
  address: string | null;
  locality: string | null;
  lat: number | null;
  lng: number | null;
  types: string[];
  /** Área alargada confirmada no momento da escolha (centro da localidade original). */
  confirmedArea: ExpandedArea | null;
};

/** Hash estável e pequeno (djb2 duplo) para assinaturas determinísticas. */
export function stableHash(value: string): string {
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    h1 = (h1 * 33) ^ code;
    h2 = (h2 * 31) ^ code;
  }
  return `${(h1 >>> 0).toString(36)}${(h2 >>> 0).toString(36)}`;
}

function normalizeConstraint(constraint: RequiredStopConstraint): string {
  return [
    constraint.kind,
    normalizeLocality(constraint.cuisine ?? ""),
    normalizeLocality(constraint.activityType ?? ""),
    normalizeLocality(constraint.query).slice(0, 160),
  ].join(":");
}

/**
 * Assinatura do pedido original normalizado. Calculada APENAS a partir de
 * texto, localidade, dia/janela e restrições — nunca de área ativa, seleções
 * fixas ou cartões.
 */
export function availabilityRequestSignature(input: {
  requestText: string;
  localityLabel: string;
  day: number;
  startMinutes: number;
  endMinutes: number;
  constraints: RequiredStopConstraint[];
}): string {
  const parts = [
    normalizeLocality(input.requestText).slice(0, 400),
    normalizeLocality(input.localityLabel),
    String(input.day),
    String(input.startMinutes),
    String(input.endMinutes),
    ...input.constraints.map(normalizeConstraint),
  ];
  return `rs_${stableHash(parts.join("|"))}`;
}

/** Identidade estável da restrição dentro do pedido (nunca o índice). */
export function constraintIdOf(
  constraint: RequiredStopConstraint,
  requestSignature: string,
): string {
  return `c_${stableHash(`${requestSignature}#${normalizeConstraint(constraint)}`)}`;
}

/** A seleção fixa corresponde a ESTA restrição deste MESMO pedido? */
export function matchesPinnedSelection(
  selection: PinnedSelectionRef,
  constraint: RequiredStopConstraint,
  requestSignature: string,
): boolean {
  return (
    selection.requestSignature === requestSignature &&
    selection.constraintId === constraintIdOf(constraint, requestSignature)
  );
}

/**
 * Área alargada pela escolha de um candidato: MANTÉM o centro/identidade da
 * localidade pedida e aumenta o raio apenas o necessário para incluir o ponto
 * escolhido, com teto de 25 km nesta etapa.
 */
export function pinnedAreaIncludingPoint(input: {
  requestedLocality: string;
  /** Centro da localidade originalmente pedida (nunca o do local escolhido). */
  center: { lat: number; lng: number };
  point: { lat: number; lng: number };
  placeLocality?: string | null | undefined;
  currentRadiusMeters?: number | null | undefined;
  language?: string | undefined;
}): ExpandedArea {
  const distance = haversineMeters(input.center, input.point);
  const radiusMeters = Math.min(
    MAX_PINNED_AREA_RADIUS_METERS,
    Math.max(
      input.currentRadiusMeters ?? 0,
      Math.round(distance + PINNED_AREA_BUFFER_METERS),
      1000,
    ),
  );
  const placeLocality = (input.placeLocality ?? "").trim();
  const includePlaceLocality =
    placeLocality &&
    normalizeLocality(placeLocality) !== normalizeLocality(input.requestedLocality);
  return {
    label: expandedAreaLabel(
      input.requestedLocality,
      includePlaceLocality ? placeLocality : null,
      input.language,
    ),
    localities: [input.requestedLocality, ...(includePlaceLocality ? [placeLocality] : [])].slice(
      0,
      6,
    ),
    center: input.center,
    radiusMeters,
  };
}

/** Cabe incluir este ponto nesta etapa de alargamento (teto de 25 km)? */
export function pinnedPointWithinCap(
  center: { lat: number; lng: number },
  point: { lat: number; lng: number },
): boolean {
  return haversineMeters(center, point) + PINNED_AREA_BUFFER_METERS <= MAX_PINNED_AREA_RADIUS_METERS;
}

/** Motivo concreto de rejeição de uma seleção fixa (nunca descarte silencioso). */
export type PinnedRejectReason =
  | "unknown_constraint"
  | "signature_mismatch"
  | "invalid_selection_ref"
  | "place_not_found"
  | "type_mismatch"
  | "outside_area"
  | "technical_failure";

/**
 * Identidade canónica de um Place ID devolvido pelo Google Places. O Text
 * Search e o Place Details do Places API (New) devolvem o mesmo ID simples
 * ("ChIJ…"), mas respostas/proxies podem trazer o resource name completo
 * ("places/ChIJ…"). O canonical devolve SEMPRE o ID simples, para que o
 * Details seja construído sobre o identificador que o próprio Google
 * confirmou — e nunca sobre um identificador adulterado pelo cliente.
 */
export function canonicalPlaceId(placeId: string | null | undefined): string {
  const raw = (placeId ?? "").trim();
  if (!raw) return "";
  const prefixed = raw.match(/^places\/(.+)$/);
  return prefixed ? prefixed[1]! : raw;
}

/** Resultado explícito de cada seleção enviada ao servidor. */
export type PinnedOutcome = {
  constraintId: string;
  placeId: string;
  accepted: boolean;
  reason: PinnedRejectReason | null;
};

/**
 * Avalia uma seleção fixa contra as evidências re-confirmadas no servidor.
 * A seleção só é aceite quando a referência é válida, o local existe, satisfaz
 * o tipo da restrição e respeita a geometria da área ativa.
 */
export function evaluatePinnedCandidate(input: {
  /** Referência assinada do servidor: válida? (false ⇒ rejeição imediata) */
  selectionRefValid: boolean;
  detailsFound: boolean;
  typeMatch: boolean;
  withinActiveArea: boolean;
}): { accepted: boolean; reason: PinnedRejectReason | null } {
  if (!input.selectionRefValid) return { accepted: false, reason: "invalid_selection_ref" };
  if (!input.detailsFound) return { accepted: false, reason: "place_not_found" };
  if (!input.typeMatch) return { accepted: false, reason: "type_mismatch" };
  if (!input.withinActiveArea) return { accepted: false, reason: "outside_area" };
  return { accepted: true, reason: null };
}
